/**
 * The Tickets card on a test job (PT-5, fieldiq-api#932): the job's ticket
 * mode, its field tickets (`GET /jobs/:id/tickets`), and the two ways a ticket
 * is made by hand.
 *
 * TICKET MODE is the job's snapshot (`ticket_mode`). The office may change it
 * until the job's first field ticket exists; after that the API answers 409
 * `ticket_mode_locked` and its sentence is shown here. The card does not decide
 * the lock from the ticket list — the API does.
 *
 * MAKING A TICKET:
 *  - per_day, active: "Close out today" (the tenant's today, `meta.today`) —
 *    the day's ticket, possibly with no automatic lines (a standby day).
 *  - complete: "Build new ticket" (PT_DISPATCH) — the replacement after a void.
 *    The API refuses `ticket_exists` / `nothing_to_ticket` and says why.
 * A per-job ticket is created by the API when the job completes; nothing here
 * creates it.
 *
 * A ticket in the list, or one just made, opens on its own page
 * (/pressure-testing/tickets/:ticketId), whose back arrow returns here.
 */
import React, { useEffect, useState } from 'react';
import { CalendarCheck, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useClosePtDay, useCreatePtTicket, usePtJobTickets, useUpdatePtJob } from '../../hooks/usePressureTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PT_TICKET_MODE_LABEL, ptSubtotalText, ptTicketModeLabel, ptTicketStatusSkin } from '../../lib/pressure-testing';
import { PT_TICKET_MODES, type PtJobDetail, type PtTicketMode } from '../../types/pressure-testing';
import { PT_INPUT, PT_SECONDARY, PtPanel, PtSkinBadge } from './PtBits';
import { ptWriteError } from './PtJobDialogs';
import { ptTicketDate } from './FieldTicketsScreen';
import { useOpenRecord } from '../downhole/recordPage';

export function PtJobTicketsCard({
  job,
  canDispatch,
  timeZone,
  today,
}: {
  job: PtJobDetail;
  canDispatch: boolean;
  timeZone: string | null;
  today: string | null;
}) {
  const tickets = usePtJobTickets(job.id);
  const update = useUpdatePtJob();
  const closeDay = useClosePtDay();
  const build = useCreatePtTicket();
  const openRecord = useOpenRecord();
  const openTicket = (id: string) => openRecord(`/pressure-testing/tickets/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<string>(job.ticket_mode ?? '');
  const failure = queryLoadFailure(tickets);
  const rows = tickets.data?.data ?? [];
  const editable = job.status === 'scheduled' || job.status === 'active';
  const tz = tickets.data?.meta.time_zone ?? timeZone;
  const day = tickets.data?.meta.today ?? today;

  useEffect(() => setMode(job.ticket_mode ?? ''), [job.ticket_mode]);

  const saveMode = () => {
    if (!mode || mode === job.ticket_mode) return;
    setError(null);
    update.mutate(
      { id: job.id, body: { ticket_mode: mode as PtTicketMode } },
      {
        onSuccess: () => toast.success(`${job.job_number} is now ticketed ${ptTicketModeLabel(mode).toLowerCase()}`),
        onError: (err) => {
          setMode(job.ticket_mode ?? '');
          setError(ptWriteError(err, 'The ticket mode was not changed.').message);
        },
      },
    );
  };

  const onCloseDay = () => {
    if (!day) return;
    setError(null);
    closeDay.mutate(
      { jobId: job.id, workDate: day },
      {
        onSuccess: (res) => {
          toast.success(res.data?.ticket_number ? `Today is on ${res.data.ticket_number}` : 'Today is closed out');
          if (res.data?.id) openTicket(res.data.id);
        },
        onError: (err) => setError(ptWriteError(err, 'Today was not closed out.').message),
      },
    );
  };

  const onBuild = () => {
    setError(null);
    build.mutate(
      { jobId: job.id },
      {
        onSuccess: (res) => {
          toast.success(res.data?.ticket_number ? `${res.data.ticket_number} drafted` : 'Ticket drafted');
          if (res.data?.id) openTicket(res.data.id);
        },
        onError: (err) => setError(ptWriteError(err, 'No ticket was built.').message),
      },
    );
  };

  const actions = (
    <div className="flex flex-wrap gap-2">
      {job.ticket_mode === 'per_day' && job.status === 'active' && (
        <button type="button" className={PT_SECONDARY} onClick={onCloseDay} disabled={!day || closeDay.isPending} data-testid="pt-job-close-day">
          {closeDay.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CalendarCheck className="size-4" aria-hidden="true" />}
          Close out today
        </button>
      )}
      {canDispatch && job.status === 'complete' && (
        <button type="button" className={PT_SECONDARY} onClick={onBuild} disabled={build.isPending} data-testid="pt-job-build-ticket">
          {build.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
          Build new ticket
        </button>
      )}
    </div>
  );

  return (
    <PtPanel eyebrow="Field tickets" title="Tickets" testId="pt-job-tickets" actions={actions}>
      <div className="space-y-4 px-4 py-4">
        <div className="flex flex-wrap items-end gap-3" data-testid="pt-job-ticket-mode">
          <div>
            <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ticket mode</p>
            {canDispatch && editable ? (
              <div className="mt-1 flex items-center gap-2">
                <select
                  className={`${PT_INPUT} w-40`}
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  aria-label="Ticket mode"
                  data-testid="pt-job-ticket-mode-select"
                >
                  {!job.ticket_mode && <option value="">—</option>}
                  {PT_TICKET_MODES.map((m) => (
                    <option key={m} value={m}>
                      {PT_TICKET_MODE_LABEL[m]}
                    </option>
                  ))}
                </select>
                {mode !== (job.ticket_mode ?? '') && (
                  <button type="button" className={PT_SECONDARY} onClick={saveMode} disabled={update.isPending} data-testid="pt-job-ticket-mode-save">
                    {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                    Save
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-1 text-sm font-semibold text-white" data-testid="pt-job-ticket-mode-value">
                {ptTicketModeLabel(job.ticket_mode)}
              </p>
            )}
          </div>
          <p className="max-w-xl text-xs text-fieldiq-muted">
            {job.ticket_mode === 'per_day'
              ? 'One ticket per work day, closed out in the field or here; completing the job sweeps anything left onto a final ticket.'
              : job.ticket_mode === 'per_job'
                ? 'One ticket for the whole job, drafted when the job is completed.'
                : ''}
            {editable ? " The mode can change until the job's first ticket exists." : ''}
          </p>
        </div>

        {error && (
          <p role="alert" data-testid="pt-job-tickets-error" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}

        {tickets.isLoading ? (
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the job's tickets" />
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(tickets)} kind={failure} subject="this job's tickets" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-fieldiq-text" data-testid="pt-job-tickets-none">
            {job.status === 'cancelled'
              ? 'This job was cancelled and has no field tickets.'
              : job.ticket_mode === 'per_day'
                ? 'No days are closed out yet.'
                : job.status === 'complete'
                  ? 'This job has no field ticket.'
                  : 'The field ticket is drafted when this job is completed.'}
          </p>
        ) : (
          <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {rows.map((t) => {
              const d = ptTicketDate(t, tz);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => openTicket(t.id)}
                    data-testid="pt-job-ticket"
                    className={`flex w-full flex-wrap items-center gap-3 border-l-4 px-3 py-2.5 text-left outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${ptTicketStatusSkin(t.status).rail}`}
                  >
                    <span className="font-mono text-sm font-extrabold text-white">{t.ticket_number}</span>
                    <PtSkinBadge skin={ptTicketStatusSkin(t.status)} value={t.status} />
                    <span className="text-sm text-fieldiq-text">
                      {d.sub} {d.text}
                    </span>
                    <span className="ml-auto font-mono text-sm tabular-nums text-white">{ptSubtotalText(t)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </PtPanel>
  );
}
